/**
 * Company-owned charter trip numbers — user-facing ops identity.
 * Under-the-hood tickets stay on UUID / `ns-*` ids. The board speaks
 * prefix + sequential (CEL1047 / NSR1000) plus route and tail separately.
 *
 * Ferries that exist for a revenue trip share that trip’s sequence with an
 * F suffix (CEL1047F, CEL1047F2) unless the company picks a separate ferry
 * range. Standalone mx / positioning ferries get their own sequence and an F.
 *
 * Celtic / CEL is a fiction catalog default for existing Celtic saves only.
 * New companies mint with the prefix the user picks — never a hard CEL lock.
 */
import type { CompanyProfile, FerryNumberMode, Mission, MissionStatus } from "./types";

/** Fiction / legacy default — existing Celtic saves and unschemed format() calls. */
export const FLIGHT_NUMBER_PREFIX = "CEL";
export const FIRST_FLIGHT_SEQ = 1001;
export const FIRST_FERRY_SEQ = 2001;

export type FlightNumberScheme = {
  prefix: string;
  startSeq: number;
  digitWidth: 3 | 4;
  ferryMode: FerryNumberMode;
  ferryStartSeq: number;
};

export type FlightNumberAssignOpts = {
  scheme?: FlightNumberScheme;
  nextFerrySeq?: number;
};

export type FlightNumberParts = {
  seq: number;
  ferry: boolean;
  /** 0 = PREFIX1047F, 1 = PREFIX1047F2 */
  ferryIndex: number;
  prefix?: string;
  digitCount?: number;
};

export type FlightNumbered = {
  id?: string;
  callsign?: string;
  flightNumber?: string;
  flightSeq?: number;
  flightType?: string;
  registration?: string;
  originIcao?: string;
  destIcao?: string;
  parentMissionId?: string;
  createdAt?: string;
  status?: MissionStatus | string;
};

export const LEGACY_CELTIC_FLIGHT_SCHEME: FlightNumberScheme = {
  prefix: FLIGHT_NUMBER_PREFIX,
  startSeq: FIRST_FLIGHT_SEQ,
  digitWidth: 4,
  ferryMode: "f_suffix",
  ferryStartSeq: FIRST_FERRY_SEQ,
};

/** New / first-run — prefix blank until the user picks. */
export const DEFAULT_FLIGHT_NUMBER_SCHEME: FlightNumberScheme = {
  prefix: "",
  startSeq: FIRST_FLIGHT_SEQ,
  digitWidth: 4,
  ferryMode: "f_suffix",
  ferryStartSeq: FIRST_FERRY_SEQ,
};

export const FLIGHT_NUMBER_PRESETS: ReadonlyArray<{
  id: string;
  label: string;
  scheme: FlightNumberScheme;
}> = [
  { id: "cel", label: "CEL 1001+", scheme: { ...LEGACY_CELTIC_FLIGHT_SCHEME } },
  {
    id: "nsr",
    label: "NSR 1000+",
    scheme: { prefix: "NSR", startSeq: 1000, digitWidth: 4, ferryMode: "f_suffix", ferryStartSeq: 2000 },
  },
];

export function normalizeFlightHint(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s._-]+/g, "");
}

export function normalizeFlightPrefix(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .slice(0, 4);
}

export function flightPrefixValid(raw: string | null | undefined): boolean {
  const p = normalizeFlightPrefix(raw);
  return p.length >= 2 && p.length <= 4;
}

export function hydrateFlightNumberDigits(raw: unknown): 3 | 4 {
  return raw === 3 ? 3 : 4;
}

export function hydrateFerryNumberMode(raw: unknown): FerryNumberMode {
  return raw === "separate_range" ? "separate_range" : "f_suffix";
}

export function hydrateFlightStart(raw: unknown, fallback = FIRST_FLIGHT_SEQ): number {
  const n = typeof raw === "number" && Number.isFinite(raw) ? Math.floor(raw) : fallback;
  return Math.max(1, Math.min(9999, n));
}

/**
 * Missing prefix on a completed / legacy company keeps CEL. New first-run
 * companies stay blank until the user picks.
 */
export function hydrateFlightNumberScheme(
  raw?: Partial<CompanyProfile> | Partial<FlightNumberScheme> | null,
  opts: { setupComplete?: boolean } = {},
): FlightNumberScheme {
  const setupComplete = opts.setupComplete === true || raw && "setupComplete" in raw && Boolean((raw as Partial<CompanyProfile>).setupComplete);
  const hasPrefixField = raw != null && "flightNumberPrefix" in raw;
  const hasSchemePrefix = raw != null && "prefix" in raw;
  let prefix = "";
  if (hasPrefixField) prefix = normalizeFlightPrefix((raw as Partial<CompanyProfile>).flightNumberPrefix);
  else if (hasSchemePrefix) prefix = normalizeFlightPrefix((raw as Partial<FlightNumberScheme>).prefix);
  else if (setupComplete) prefix = FLIGHT_NUMBER_PREFIX;
  const startSeq = hydrateFlightStart(
    (raw as Partial<CompanyProfile> | undefined)?.flightNumberStart ?? (raw as Partial<FlightNumberScheme> | undefined)?.startSeq,
  );
  const digitWidth = hydrateFlightNumberDigits(
    (raw as Partial<CompanyProfile> | undefined)?.flightNumberDigits ?? (raw as Partial<FlightNumberScheme> | undefined)?.digitWidth,
  );
  const ferryMode = hydrateFerryNumberMode(
    (raw as Partial<CompanyProfile> | undefined)?.ferryNumberMode ?? (raw as Partial<FlightNumberScheme> | undefined)?.ferryMode,
  );
  const ferryStartSeq = hydrateFlightStart(
    (raw as Partial<CompanyProfile> | undefined)?.ferryStartSeq ?? (raw as Partial<FlightNumberScheme> | undefined)?.ferryStartSeq,
    FIRST_FERRY_SEQ,
  );
  return { prefix, startSeq, digitWidth, ferryMode, ferryStartSeq };
}

export function companyFlightNumberScheme(
  company?: Partial<CompanyProfile> | null,
): FlightNumberScheme {
  return hydrateFlightNumberScheme(company, { setupComplete: company?.setupComplete });
}

export function schemePreview(scheme: FlightNumberScheme, opts?: { ferry?: boolean }): string {
  const n = formatFlightNumber(scheme.startSeq, { ferry: opts?.ferry }, scheme);
  return n || `${scheme.startSeq}`;
}

function escapeRe(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function formatFlightNumber(
  seq: number,
  opts?: { ferry?: boolean; ferryIndex?: number },
  scheme: FlightNumberScheme = LEGACY_CELTIC_FLIGHT_SCHEME,
): string {
  const n = Math.max(1, Math.floor(seq));
  const prefix = normalizeFlightPrefix(scheme.prefix);
  const digits = String(n).padStart(scheme.digitWidth, "0");
  const base = `${prefix}${digits}`;
  if (!opts?.ferry) return base;
  const idx = opts.ferryIndex ?? 0;
  return idx <= 0 ? `${base}F` : `${base}F${idx + 1}`;
}

export function parseFlightNumber(
  raw: string | null | undefined,
  prefix?: string,
): FlightNumberParts | null {
  const n = normalizeFlightHint(raw);
  const wanted = normalizeFlightPrefix(prefix);
  const match = wanted
    ? n.match(new RegExp(`^(${escapeRe(wanted)})(\\d{2,})(F(\\d+)?)?$`))
    : n.match(/^([A-Z]{2,4})(\d{2,})(F(\d+)?)?$/);
  if (!match) return null;
  const seq = Number(match[2]);
  if (!Number.isFinite(seq) || seq < 1) return null;
  const ferry = Boolean(match[3]);
  const ferryIndex = match[4] ? Math.max(0, Number(match[4]) - 1) : 0;
  return { seq, ferry, ferryIndex, prefix: match[1], digitCount: match[2]?.length ?? 0 };
}

/**
 * `parseFlightNumber` under a company scheme. A blank-prefix scheme (the
 * first-run default) issues bare `1001` / `1001F`, so those parse too.
 */
export function parseSchemeFlightNumber(
  raw: string | null | undefined,
  scheme?: FlightNumberScheme,
): FlightNumberParts | null {
  const parts = parseFlightNumber(raw, scheme?.prefix || undefined);
  if (parts || !scheme || normalizeFlightPrefix(scheme.prefix)) return parts;
  const match = normalizeFlightHint(raw).match(/^(\d{3,})(F(\d+)?)?$/);
  if (!match) return null;
  const seq = Number(match[1]);
  if (!Number.isFinite(seq) || seq < 1) return null;
  const ferryIndex = match[3] ? Math.max(0, Number(match[3]) - 1) : 0;
  return { seq, ferry: Boolean(match[2]), ferryIndex, prefix: "", digitCount: match[1]?.length ?? 0 };
}

/** Canonical prefix + 3+ digits — not a tail and not the old minute-based CEL05F. */
export function isCanonicalFlightNumber(
  raw: string | null | undefined,
  scheme?: FlightNumberScheme,
): boolean {
  const parts = parseSchemeFlightNumber(raw, scheme);
  if (!parts) return false;
  const width = scheme?.digitWidth ?? 3;
  const floor = scheme?.startSeq ?? FIRST_FLIGHT_SEQ;
  const digits = parts.digitCount ?? String(parts.seq).length;
  if (digits < Math.min(width, 3)) return false;
  return parts.seq >= Math.min(floor, 100) || digits >= 3;
}

export function needsFlightNumberMigration(
  trip: Pick<FlightNumbered, "callsign" | "flightNumber" | "flightSeq">,
  scheme?: FlightNumberScheme,
): boolean {
  if (isCanonicalFlightNumber(trip.flightNumber, scheme)) return false;
  if (isCanonicalFlightNumber(trip.flightNumber)) return false;
  if (typeof trip.flightSeq === "number" && trip.flightSeq >= (scheme?.startSeq ?? FIRST_FLIGHT_SEQ)) {
    if (isCanonicalFlightNumber(trip.callsign, scheme) || isCanonicalFlightNumber(trip.callsign)) return false;
  }
  if (isCanonicalFlightNumber(trip.callsign, scheme) || isCanonicalFlightNumber(trip.callsign)) {
    return !trip.flightNumber;
  }
  return true;
}

export function displayFlightNumber(trip: Pick<FlightNumbered, "flightNumber" | "callsign">): string {
  return (trip.flightNumber || trip.callsign || "").trim();
}

/** One-ticket identity for Support / Release / Megan / pop-outs — never an Inbox RFQ id. */
export function missionTicketRef(mission: Pick<FlightNumbered, "id" | "flightNumber" | "callsign">): {
  missionId: string;
  flightNumber: string;
} {
  return {
    missionId: (mission.id ?? "").trim(),
    flightNumber: displayFlightNumber(mission),
  };
}

export function tripRouteLabel(trip: Pick<FlightNumbered, "originIcao" | "destIcao">): string {
  const o = (trip.originIcao ?? "").trim().toUpperCase();
  const d = (trip.destIcao ?? "").trim().toUpperCase();
  if (!o || !d) return "";
  return `${o}–${d}`;
}

/**
 * Spoken / board readback: flight number, tail, route — never a bare registration
 * as the only id.
 */
export function spokenTripLabel(
  trip: Pick<FlightNumbered, "flightNumber" | "callsign" | "registration" | "originIcao" | "destIcao">,
): string {
  const fn = displayFlightNumber(trip) || "trip";
  const tail = normalizeFlightHint(trip.registration);
  const fnNorm = normalizeFlightHint(fn);
  const tailBit = tail && tail !== fnNorm ? String(trip.registration ?? "").trim().toUpperCase() : "";
  const route = tripRouteLabel(trip);
  return [fn, tailBit, route].filter(Boolean).join(", ");
}

export function applyFlightIdentity<T extends FlightNumbered>(
  trip: T,
  flightNumber: string,
  seq: number,
): T {
  return { ...trip, flightNumber, flightSeq: seq, callsign: flightNumber };
}

function isFerryTrip(trip: FlightNumbered): boolean {
  return trip.flightType === "ferry";
}

function createdStamp(trip: FlightNumbered): number {
  const ms = Date.parse(trip.createdAt ?? "");
  return Number.isFinite(ms) ? ms : 0;
}

export function nextSeqAfter(values: Iterable<number>, floor = FIRST_FLIGHT_SEQ): number {
  let max = floor - 1;
  for (const n of values) {
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
}

export function usedFlightSeqs(trips: readonly FlightNumbered[]): number[] {
  const out: number[] = [];
  for (const trip of trips) {
    const parts = parseSchemeFlightNumber(trip.flightNumber || trip.callsign, DEFAULT_FLIGHT_NUMBER_SCHEME);
    if (parts && (parts.digitCount ?? 0) >= 3) out.push(parts.seq);
    else if (typeof trip.flightSeq === "number" && trip.flightSeq >= 1) out.push(trip.flightSeq);
  }
  return out;
}

function nextFerryIndex(seq: number, trips: readonly FlightNumbered[]): number {
  const used = new Set<number>();
  for (const trip of trips) {
    // A cancelled ferry does not hold its slot — otherwise a cancel / re-file
    // cycle walks the suffix up (CEL1007F2 … CEL1007F7) and reads like a mash.
    if (trip.status === "cancelled") continue;
    const parts = parseSchemeFlightNumber(trip.flightNumber || trip.callsign, DEFAULT_FLIGHT_NUMBER_SCHEME);
    if (parts && parts.seq === seq && parts.ferry) used.add(parts.ferryIndex);
  }
  let idx = 0;
  while (used.has(idx)) idx += 1;
  return idx;
}

function parentSeqFor(trip: FlightNumbered, pool: readonly FlightNumbered[], floor: number): number | null {
  if (!trip.parentMissionId) return null;
  const parent = pool.find((m) => m.id === trip.parentMissionId);
  if (!parent) return null;
  if (typeof parent.flightSeq === "number" && parent.flightSeq >= floor) return parent.flightSeq;
  const parts = parseFlightNumber(parent.flightNumber || parent.callsign);
  return parts && parts.seq >= floor ? parts.seq : null;
}

function resolveScheme(opts?: FlightNumberAssignOpts | FlightNumberScheme): FlightNumberScheme {
  if (!opts) return LEGACY_CELTIC_FLIGHT_SCHEME;
  if ("prefix" in opts && "startSeq" in opts && "digitWidth" in opts) return opts;
  return opts.scheme ?? LEGACY_CELTIC_FLIGHT_SCHEME;
}

/**
 * Stamp incoming tickets (inbox accept, ferry insert, mx ferry) with company numbers.
 * Existing canonical numbers are left alone. Ferries inherit the parent seq + F
 * unless the company uses a separate ferry range.
 */
export function assignFlightNumbersToTrips<T extends FlightNumbered>(
  incoming: T[],
  existing: readonly T[],
  nextSeqInput: number,
  opts?: FlightNumberAssignOpts,
): { trips: T[]; nextFlightSeq: number; nextFerrySeq: number } {
  const scheme = resolveScheme(opts);
  const floor = scheme.startSeq;
  const pool: FlightNumbered[] = [...existing];
  let nextSeq = Math.max(floor, Math.floor(nextSeqInput) || floor);
  const known = usedFlightSeqs(pool);
  nextSeq = Math.max(nextSeq, nextSeqAfter(known, floor));
  let nextFerrySeq = Math.max(
    scheme.ferryStartSeq,
    Math.floor(opts?.nextFerrySeq ?? scheme.ferryStartSeq) || scheme.ferryStartSeq,
  );
  const trips = incoming.map((trip) => {
    if (!needsFlightNumberMigration(trip, scheme)) {
      const parts = parseSchemeFlightNumber(trip.flightNumber || trip.callsign, scheme);
      const seq = parts?.seq ?? trip.flightSeq ?? nextSeq;
      const numbered = applyFlightIdentity(trip, trip.flightNumber || trip.callsign || formatFlightNumber(seq, undefined, scheme), seq);
      pool.push(numbered);
      return numbered;
    }
    if (isFerryTrip(trip)) {
      if (scheme.ferryMode === "separate_range") {
        const seq = nextFerrySeq;
        nextFerrySeq += 1;
        const numbered = applyFlightIdentity(trip, formatFlightNumber(seq, { ferry: true, ferryIndex: 0 }, scheme), seq);
        pool.push(numbered);
        return numbered;
      }
      const parentSeq = parentSeqFor(trip, [...pool, ...incoming], Math.min(floor, 1));
      if (parentSeq) {
        const ferryIndex = nextFerryIndex(parentSeq, [...pool, ...incoming]);
        const flightNumber = formatFlightNumber(parentSeq, { ferry: true, ferryIndex }, scheme);
        const numbered = applyFlightIdentity(trip, flightNumber, parentSeq);
        pool.push(numbered);
        return numbered;
      }
      const seq = nextSeq;
      nextSeq += 1;
      const numbered = applyFlightIdentity(trip, formatFlightNumber(seq, { ferry: true, ferryIndex: 0 }, scheme), seq);
      pool.push(numbered);
      return numbered;
    }
    const seq = nextSeq;
    nextSeq += 1;
    const numbered = applyFlightIdentity(trip, formatFlightNumber(seq, undefined, scheme), seq);
    pool.push(numbered);
    return numbered;
  });
  return { trips, nextFlightSeq: nextSeq, nextFerrySeq };
}

/**
 * Rewrite a persisted board so every ticket has a company ops number and `callsign`
 * matches it. Tails stay on `registration`. Existing CEL / other prefixes stay.
 */
export function migrateBoardFlightNumbers<T extends FlightNumbered>(
  missions: T[],
  nextSeqInput: number = FIRST_FLIGHT_SEQ,
  opts?: FlightNumberAssignOpts,
): { missions: T[]; nextFlightSeq: number; nextFerrySeq: number } {
  const scheme = resolveScheme(opts);
  const floor = scheme.startSeq;
  if (!missions.length) {
    const nextFlightSeq = Math.max(floor, Math.floor(nextSeqInput) || floor);
    return {
      missions,
      nextFlightSeq,
      nextFerrySeq: Math.max(scheme.ferryStartSeq, opts?.nextFerrySeq ?? scheme.ferryStartSeq),
    };
  }
  const ordered = [...missions].sort((a, b) => createdStamp(a) - createdStamp(b));
  const keep = new Map<string, T>();
  const incoming: T[] = [];
  const existingCanonical: T[] = [];
  for (const trip of ordered) {
    if (!needsFlightNumberMigration(trip, scheme) && trip.id) {
      const parts = parseSchemeFlightNumber(trip.flightNumber || trip.callsign, scheme);
      const seq = parts?.seq ?? trip.flightSeq ?? floor;
      const numbered = applyFlightIdentity(trip, trip.flightNumber || trip.callsign || formatFlightNumber(seq, undefined, scheme), seq);
      keep.set(trip.id, numbered);
      existingCanonical.push(numbered);
    } else {
      incoming.push(trip);
    }
  }
  const revenue = incoming.filter((t) => !isFerryTrip(t));
  const ferries = incoming.filter((t) => isFerryTrip(t));
  const first = assignFlightNumbersToTrips(revenue, existingCanonical, nextSeqInput, opts);
  const second = assignFlightNumbersToTrips(ferries, [...existingCanonical, ...first.trips], first.nextFlightSeq, {
    scheme,
    nextFerrySeq: first.nextFerrySeq,
  });
  for (const trip of [...first.trips, ...second.trips]) {
    if (trip.id) keep.set(trip.id, trip);
  }
  return {
    missions: missions.map((m) => (m.id && keep.has(m.id) ? keep.get(m.id)! : m)),
    nextFlightSeq: second.nextFlightSeq,
    nextFerrySeq: second.nextFerrySeq,
  };
}

export function hydrateNextFlightSeq(
  raw: unknown,
  missions: readonly FlightNumbered[] = [],
  floor = FIRST_FLIGHT_SEQ,
): number {
  const n = typeof raw === "number" && Number.isFinite(raw) ? Math.floor(raw) : floor;
  return Math.max(floor, n, nextSeqAfter(usedFlightSeqs(missions), floor));
}

export function withCompanyFlightSeq<T extends { nextFlightSeq?: number; nextFerrySeq?: number }>(
  company: T,
  nextFlightSeq: number,
  nextFerrySeq?: number,
): T {
  return {
    ...company,
    nextFlightSeq,
    ...(typeof nextFerrySeq === "number" ? { nextFerrySeq } : {}),
  };
}

export function stampMissionsAndCompany<T extends FlightNumbered, C extends Partial<CompanyProfile> & { nextFlightSeq?: number }>(
  missions: T[],
  company: C,
): { missions: T[]; company: C } {
  const scheme = companyFlightNumberScheme(company);
  const migrated = migrateBoardFlightNumbers(missions, hydrateNextFlightSeq(company.nextFlightSeq, missions, scheme.startSeq), {
    scheme,
    nextFerrySeq: (company as { nextFerrySeq?: number }).nextFerrySeq,
  });
  return {
    missions: migrated.missions,
    company: withCompanyFlightSeq(company, migrated.nextFlightSeq, migrated.nextFerrySeq),
  };
}

export function asMissionFlightFields(trip: FlightNumbered): Pick<Mission, "callsign" | "flightNumber" | "flightSeq"> {
  const fn = displayFlightNumber(trip);
  return {
    callsign: fn,
    flightNumber: fn || undefined,
    flightSeq: trip.flightSeq,
  };
}
